import { FunctionComponent, memo } from "react";
import MetricBox from "components-for/display/metric-box";
import { parseUrlArray } from "@edgetier/utilities";
import { useQueryParameters } from "@edgetier/client-components";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { calculateValue } from "../agent-metrics.utilities";
import { IProps } from "./agent-writing-issue-metric.types";
import "./agent-writing-issue-metric.scss";

/**
 * Show summary metrics for an agent interaction grammar and spelling errors
 * @param props.icon        Metric icon.
 * @param props.title       Name of the metric.
 * @param props.tooltipContent  Details about the metric value shown on mouse over
 * @param props.writingIssueType  Type of writing mistake sent to request
 */
const AgentWritingIssueMetric: FunctionComponent<IProps> = ({
    defaultStartDateTime,
    defaultEndDateTime,
    icon,
    writingIssueType,
    title,
    tooltipContent,
}) => {
    const { comparisonAgentId, reviewAgentId } = useQueryParameters({
        comparisonAgentId: parseUrlArray,
        reviewAgentId: String,
    });

    const interactionParameters = useInteractionQueryParameters({
        defaults: { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime, agentId: reviewAgentId },
    });

    const agentIssueParameters: Record<string, any> = {
        ...interactionParameters,
        agentId: reviewAgentId,
    };
    const comparisonIssueParameters: Record<string, any> = {
        ...interactionParameters,
        agentId: comparisonAgentId,
    };

    // Total number of interactions for an agent
    const { data: agentInteractionsCount, isLoading: isAgentInteractionsCountLoading } =
        useInteractionsCount(agentIssueParameters);

    // Total number of interactions for everyone
    const { data: comparisonInteractionsCount, isLoading: isComparisonInteractionsCountLoading } =
        useInteractionsCount(comparisonIssueParameters);

    // Request the number of interactions with mistakes of given type
    const agentWritingMistakeParameters: Record<string, any> = {
        ...agentIssueParameters,
        writingIssueType,
    };
    const { data: agentWritingMistakeCount, isLoading: isAgentWritingMistakeCountLoading } =
        useInteractionsCount(agentWritingMistakeParameters);

    // Request the number of interactions with mistakes of given type for everyone
    const comparisonWritingMistakeParameters: Record<string, any> = {
        ...comparisonIssueParameters,
        writingIssueType,
    };
    const { data: comparisonWritingMistakeCount, isLoading: isComparisonWritingMistakeCountLoading } =
        useInteractionsCount(comparisonWritingMistakeParameters);

    return (
        <MetricBox
            icon={icon}
            isLoading={
                isAgentInteractionsCountLoading ||
                isComparisonInteractionsCountLoading ||
                isAgentWritingMistakeCountLoading ||
                isComparisonWritingMistakeCountLoading
            }
            options={{ maximumFractionDigits: 0 }}
            comparisonValue={calculateValue(comparisonInteractionsCount, comparisonWritingMistakeCount)}
            title={title}
            tooltipContent={tooltipContent}
            unit="%"
            value={calculateValue(agentInteractionsCount, agentWritingMistakeCount)}
        />
    );
};

export default memo(AgentWritingIssueMetric);
